import React from 'react';
import { formatCurrency } from '../../utils/formatCurrency';
import { formatAmount } from '../../utils/formatPrice';
import OmrSign from './OmrSign';

const baseStyle = {
  fontVariantNumeric: 'tabular-nums',
  fontFamily: '"Plus Jakarta Sans", "IBM Plex Sans Arabic", Inter, sans-serif',
  fontWeight: 700,
  color: 'var(--primary)',
  letterSpacing: '-0.01em',
  display: 'inline-flex',
  alignItems: 'baseline',
  gap: '0.5rem',
  lineHeight: 1.2,
};

const sizeMap = {
  sm: 'var(--text-base)',
  md: 'var(--text-lg)',
  lg: 'var(--text-2xl)',
  xl: 'var(--text-3xl)',
};

const originalStyle = {
  fontSize: '0.75em',
  fontWeight: 500,
  color: 'var(--text-tertiary)',
  textDecoration: 'line-through',
};

// Inner inline-flex group that holds the OMR sign + digits.
// `direction: 'ltr'` is intentional and scoped to this group only — the official
// guideline requires the sign to sit on the physical left of the numeral in all
// languages (including Arabic RTL pages). Page-level direction is unchanged.
const omrGroupStyle = {
  display: 'inline-flex',
  alignItems: 'baseline',
  gap: '0.25em',
  direction: 'ltr',
  unicodeBidi: 'isolate',
};

const resolveCode = (currency) => {
  if (!currency) return null;
  if (typeof currency === 'string') return currency;
  return currency.code || null;
};

const PriceTag = ({
  amount,
  currency,
  locale,
  size = 'md',
  originalAmount = null,
  color,
  className,
  style,
  weight = 'medium',
  ...rest
}) => {
  const code = resolveCode(currency);
  const isOMR = code === 'OMR';
  const showOriginal = originalAmount != null && Number(originalAmount) > Number(amount);

  const wrapperStyle = {
    ...baseStyle,
    fontSize: sizeMap[size] || sizeMap.md,
    color: color || baseStyle.color,
    ...style,
  };

  if (isOMR) {
    return (
      <span className={className} style={wrapperStyle} {...rest}>
        <span style={omrGroupStyle}>
          <OmrSign weight={weight} />
          <span>{formatAmount(amount, currency)}</span>
        </span>
        {showOriginal && (
          <span style={{ ...originalStyle, ...omrGroupStyle, color: originalStyle.color }}>
            <OmrSign weight={weight} />
            <span>{formatAmount(originalAmount, currency)}</span>
          </span>
        )}
      </span>
    );
  }

  const formatted = formatCurrency(amount, currency, locale);
  const formattedOriginal = showOriginal ? formatCurrency(originalAmount, currency, locale) : null;

  return (
    <span className={className} style={wrapperStyle} {...rest}>
      <span>{formatted}</span>
      {formattedOriginal && <span style={originalStyle}>{formattedOriginal}</span>}
    </span>
  );
};

export default PriceTag;
